// Render controls INTO a module's EXISTING .module-sub-controls strip — the needsPlayer
// counterpart to <SubControls>.
//
// The two differ by who owns the strip. A needsPlayer:false module (League Table, Ranking,
// Filter, Squad) has no strip until it builds its own, so <SubControls> CREATES one (and
// removes strays). A needsPlayer:true module (Stats, later Radar) is different: main.js's
// createModule already built the strip and filled it with the shared player search/select
// (js/main.js:2314). Stats only needs to ADD a season select next to that picker — exactly
// what renderers-stats.js does by appending a .season-select-wrap to the existing strip.
// Using <SubControls> here would create a second strip and delete main.js's (taking the
// player picker with it), so this primitive appends into the existing one instead and
// touches nothing main.js put there.

import { useState, useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { watchControlsOverflow } from "../canvas/controls-overflow.js";

interface Props {
  mod: HTMLElement;
  children: ReactNode;
}

export function SubControlsSlot({ mod, children }: Props) {
  // Resolve the strip main.js already built. If it's somehow absent (not a needsPlayer
  // module), render nothing rather than fabricate one — the caller only uses this for
  // needsPlayer modules where the strip is guaranteed.
  const [strip] = useState<HTMLElement | null>(
    () => mod.querySelector<HTMLElement>(":scope > .module-sub-controls"),
  );

  // Keep the overflow watcher in sync when our controls change the strip's width (same
  // hook the legacy renderers call after building their controls).
  useEffect(() => {
    if (strip) watchControlsOverflow(mod);
  }, [mod, strip]);

  if (!strip) return null;
  // Portal children as the strip's LAST elements (after the player picker), so React owns
  // and cleans up only its own nodes and never disturbs main.js's.
  return createPortal(children, strip);
}
